import { connect } from "react-redux";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { withRouter } from "react-router-dom";

import { AVAILABLE, AWAY, BUSY, isSpecialEvent } from "@/constants/event";
import { Button2 } from "@/ui2/atoms/Button";
import { capitalizeFirstLetter } from "@/component-library/utils";
import {
  closeDrawer,
  deleteAppointment,
  editAppointmentSelected,
  updateSelectedDate,
  updateSelectedDateV2,
  updateSelectedEvents,
} from "@/redux/actions/calendar";
import { cn } from "@/lib/helpers/cn";
import dayjs from "@/lib/helpers/dayjs";
import { eventTypeDisplayNameFactory } from "@/components/Provider/ScheduleAvailability/ActiveScheduleDetails";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { Heading2 } from "@/ui2/atoms/Heading";
import { Image } from "@/component-library/Image";
import { isUserPeerOrCM } from "@/redux/actions/auth";

import { getCanStartCall } from "../../utils/general";
import { ScheduleDeleteDialog } from "../Modals/ScheduleDeleteDialog";

import CancellationInfo from "./CancellationInfo";
import "./styles.scss";

const CalendarAppointmentView = ({ calendarRect, className, event, history, ...props }) => {
  const {
    allotted_time: duration,
    callId,
    deleted: isCancelled,
    event_type: eventType,
    patient_email: patientEmail,
    patient_name: patientName,
    patient_preferred_name: patientPreferredName,
    patient_username: patientUsername,
    provider_email: providerEmail,
    series_data: seriesData,
  } = event || {};

  const [deletedCallId, setDeletedCallId] = useState(null);

  // convert event timestamp to user's local timezone
  const eventTimestamp = dayjs.utc(event?.timestamp).tz(dayjs.tz.guess());
  const participantsEmails = [patientEmail, providerEmail];

  const parsedEventType = isSpecialEvent(eventType)
    ? capitalizeFirstLetter(eventType)
    : eventTypeDisplayNameFactory(eventType);

  const isBusyEvent = eventType === BUSY;
  const isPeerOrCaseManager = isUserPeerOrCM();

  const isGuestListShown = ![AWAY, BUSY].includes(eventType);
  const isJoinButtonShown =
    isCancelled || isSpecialEvent(eventType) || isBusyEvent || isPeerOrCaseManager ? false : true;

  const hasAppointmentPast = eventTimestamp.add(duration, "minutes").isBefore(dayjs());
  const formattedEventTimestamp = eventTimestamp.format("MMMM DD, YYYY");

  const onCancel = () => setDeletedCallId(null);
  const onDelete = () => setDeletedCallId(callId);
  const onEdit = () => props.editAppointmentSelected(event);
  const onJoin = () => history.push(`/app/video-call?callId=${callId}`);

  const scheduleDeleteHandler = (cancellation_reason, twenty_four_hour_notice, delete_series) => {
    props.deleteAppointment(callId, cancellation_reason, twenty_four_hour_notice, delete_series);
    onCancel();
  };

  const isEditDeleteControlShown = () => {
    if ([AVAILABLE, AWAY].includes(eventType) && isPeerOrCaseManager) {
      return false;
    }

    // intentionally allows providers to delete available time
    // to give them complete control of their schedule
    if (eventType === AVAILABLE) return true;
    if (isCancelled || hasAppointmentPast || isBusyEvent) {
      return false;
    }

    return true;
  };

  const renderReoccurringText = () => {
    const seriesDaysArray = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday",
    ];

    const seriesDays = seriesData?.frequency_days.sort().map((day) => ` ${seriesDaysArray[day]}`);
    const frequencyWeeks = seriesData?.frequency_weeks || 0;
    const frequencyWeeksText = frequencyWeeks > 0 ? `${frequencyWeeks + 1} weeks` : "week";
    const reoccuringText = `Reoccurs every ${frequencyWeeksText} on ${seriesDays} for ${seriesData?.number_of_occurrences} times`;

    return (
      <div className="apti-CalendarAppointmentView--reoccuringTextContainer">
        <span className="apti-CalendarAppointmentView--reoccuringText">{reoccuringText}</span>
      </div>
    );
  };

  return (
    <div className={cn("apti-CalendarAppointmentView", className)}>
      {isCancelled ? <CancellationInfo calendarRect={calendarRect} event={event} /> : null}

      {!isBusyEvent && (
        <AppointmentName
          event={event}
          eventType={eventType}
          history={history}
          patientName={patientName}
          patientPreferredName={patientPreferredName}
          patientUsername={patientUsername}
        />
      )}

      <div className="apti-CalendarAppointmentView--containerDot">
        {!isBusyEvent && <span className={`apti-CalendarAppointmentView--dot ${eventType}`} />}

        <span className={`apti-CalendarAppointmentView--text ${eventType}`}>{parsedEventType}</span>
      </div>

      <section>
        <div className="apti-CalendarAppointmentView--headersContainer">
          <Image
            alt="Calendar Icon"
            className="apti-CalendarAppointmentView--icon"
            src="icons/calendar-outline.svg"
          />

          <div>
            <time dateTime={eventTimestamp.toISOString()}>{formattedEventTimestamp}</time>
            <span className="ml-2 mr-2">&#x2022;</span>
            <time dateTime={eventTimestamp.format("hh:mm")}>{eventTimestamp.format("hh:mm")}</time>
            &thinsp;&ndash;&thinsp;
            <time dateTime={eventTimestamp.add(duration, "minutes").format("hh:mm A")}>
              {eventTimestamp.add(duration, "minutes").format("hh:mm A")}
            </time>
          </div>
        </div>

        {seriesData && renderReoccurringText()}
      </section>

      {isJoinButtonShown && (
        <section className="apti-CalendarAppointmentView--container my-2">
          <Image
            alt="Camera Icon"
            className="apti-CalendarAppointmentView--icon"
            src="icons/camera-outline.svg"
          />

          <div className="apti-CalendarAppointmentView--joinButtonContainer">
            <Button2
              isDisabled={
                !getCanStartCall(
                  dayjs().utc().format("YYYY-MM-DD HH:mm"),
                  event.timestamp,
                  duration,
                )
              }
              onPress={onJoin}
              {...getAutomationDataAttr("CalendarAppointmentView_join")}
            >
              Join
            </Button2>

            <span className="apti-CalendarAppointmentView--joinButtonSubText">
              {hasAppointmentPast ? "This session ended" : "Up to 15 min before"}
            </span>
          </div>
        </section>
      )}

      {isGuestListShown && (
        <section>
          <div className="apti-CalendarAppointmentView--headers-container">
            <Image
              alt="User Icon"
              className="apti-CalendarAppointmentView--icon"
              src="icons/user-outline.svg"
            />

            <span className="apti-CalendarAppointmentView--text">Guests</span>
          </div>

          <ul className="tw:ml-6">
            {participantsEmails.map((email) => (
              <li key={email}>{email}</li>
            ))}
          </ul>
        </section>
      )}

      {isEditDeleteControlShown() && (
        <section className="tw:cols-2 tw:grid tw:grid-flow-col tw:gap-4">
          <Button2
            onPress={onDelete}
            variant="destructive-primary"
            {...getAutomationDataAttr("calendar-appointment-view-delete-button")}
          >
            Delete
          </Button2>

          <Button2
            onPress={onEdit}
            variant="secondary"
            {...getAutomationDataAttr("calendar-appointment-view-edit-button")}
          >
            Edit
          </Button2>
        </section>
      )}

      {deletedCallId && (
        <ScheduleDeleteDialog
          callId={deletedCallId}
          cancelDelete={onCancel}
          scheduleDelete={scheduleDeleteHandler}
          type={eventType === AWAY ? "away" : "video"}
        />
      )}
    </div>
  );
};

CalendarAppointmentView.propTypes = {
  calendarRect: PropTypes.object,
  className: PropTypes.string,
  event: PropTypes.object,
};

CalendarAppointmentView.defaultProps = {
  calendarRect: undefined,
  className: undefined,
  event: {},
};

const mapStateToProps = (state) => {
  return {
    calendar: state.calendar,
  };
};

const mapDispatchToProps = {
  closeDrawer,
  deleteAppointment,
  editAppointmentSelected,
  updateSelectedDate,
  updateSelectedDateV2,
  updateSelectedEvents,
};

function AppointmentName({
  event,
  eventType,
  history,
  patientName,
  patientPreferredName,
  patientUsername,
}) {
  if (eventType === AVAILABLE) return <EventHeader>{capitalizeFirstLetter(eventType)}</EventHeader>;
  if (eventType === AWAY) return <EventHeader>{event.label}</EventHeader>;

  return (
    <EventHeader
      className="tw:cursor-pointer tw:text-text-teal-primary"
      onClick={() => {
        history.push({
          pathname: `/app/patient/${patientUsername}/profile/v2`,
        });
      }}
    >
      {patientName} {patientPreferredName ? `(${patientPreferredName})` : ""}
    </EventHeader>
  );
}

function EventHeader(props) {
  return <Heading2 as="h2" size={3} {...props} />;
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CalendarAppointmentView));
